import React from 'react';
import { UserGroupIcon, UserIcon } from '@heroicons/react/24/outline';
import type { TypeAheadOption } from '../TypeAhead';
import type { AzureADGroup, AzureADUser } from '../../services/permissions.service';
import type { SelectedEntity } from '../../contexts/PermissionsModeContext';

/**
 * Who a component permission can be granted to: an enabled group, or one user. A user
 * grant gives a single person access their group doesn't have — highest level wins, so
 * it can only raise access, never take it away. Shared by the permissions-mode toolbar
 * and the per-component popover so the two pickers stay in step (users were cut from
 * both on 2026-04-15 and restored together).
 *
 * Users are `searchOnly`: every Azure AD user is enabled for permissions (592 of them,
 * against ~23 curated groups), so listing them up front buries the groups. They appear
 * once the admin types a name or email.
 */
export const buildEntityOptions = (
  groups: AzureADGroup[],
  users: AzureADUser[],
): TypeAheadOption[] => [
  ...groups.map((group) => ({
    value: `group:${group.group_id}`,
    label: group.group_name,
    description: group.description,
    icon: <UserGroupIcon className="w-5 h-5" />,
    group: 'Groups',
  })),
  ...users.map((user) => ({
    value: `user:${user.user_id}`,
    label: user.display_name,
    description: user.email,
    icon: <UserIcon className="w-5 h-5" />,
    group: 'Users',
    searchOnly: true,
  })),
];

/** A picked `group:<id>` / `user:<id>` option value as the entity it names, or null. */
export const resolveEntity = (
  value: string,
  groups: AzureADGroup[],
  users: AzureADUser[],
): SelectedEntity | null => {
  const [entityType, entityId] = value.split(':');
  if (entityType === 'group') {
    const group = groups.find((g) => g.group_id === entityId);
    return group ? { type: 'group', id: entityId, label: group.group_name } : null;
  }
  if (entityType === 'user') {
    const user = users.find((u) => u.user_id === entityId);
    return user ? { type: 'user', id: entityId, label: user.display_name } : null;
  }
  return null;
};
